(() => {
  'use strict';
  if (window.KdkGalleryPreview) { window.KdkGalleryPreview.init(document); return; }
  const instances = new Map();
  const cfg = window.KDK_SPACES_CONFIG || {};
  const selectedKey = String(cfg.storageNamespace || 'kdk_spaces_lab_v1') + ':gallery:selected';
  const validId = value => /^\d+$/.test(String(value || '')) ? String(value) : '';
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };
  const button = (className, text) => { const element = node('button', className, text); element.type = 'button'; return element; };
  function parse(element, fallback) {
    try { return JSON.parse(element?.textContent || ''); } catch (_) { return fallback; }
  }
  function selectedSpace() {
    try { return localStorage.getItem(selectedKey) || ''; } catch (_) { return ''; }
  }
  function rememberSpace(id) {
    try { if (id) localStorage.setItem(selectedKey, id); else localStorage.removeItem(selectedKey); } catch (_) {}
  }
  function indexHasSpaces() {
    try { return (window.KdkSpacesEarly?.read() || []).length > 0; } catch (_) { return false; }
  }
  class GalleryPreview {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-section-type="product"]');
      if (!this.section) return;
      this.strings = parse(root.querySelector('[data-gallery-strings]'), {});
      this.product = parse(this.section.querySelector('[data-product-json]'), {}).product || {};
      this.productId = validId(root.dataset.productId);
      this.selectedVariant = null;
      this.variant = null;
      this.rows = [];
      this.selected = null;
      this.state = 'empty';
      this.busy = false;
      this.painting = false;
      this.eligible = false;
      this.destroyed = false;
      this.sequence = 0;
      this.refreshSequence = 0;
      this.urls = new Map();
      this.availableSent = false;
      this.impressionSent = false;
      this.cacheViews = new Set();
      this.ratio = 0;
      this.visibilityTimer = null;
      this.abort = new AbortController();
      this.dialog = null;
      const on = { signal: this.abort.signal };
      this.root.addEventListener('click', event => this.handleAction(event), on);
      this.section.addEventListener('click', event => this.navigate(event), { ...on, capture: true });
      this.section.addEventListener('keydown', event => {
        if (event.key === ' ' && event.target.closest('[data-kdk-gallery-thumb]')) this.navigate(event);
      }, { ...on, capture: true });
      this.root.querySelector('[data-gallery-camera-input]')?.addEventListener('change', event => this.fileChosen(event, 'camera'), on);
      this.root.querySelector('[data-gallery-upload-input]')?.addEventListener('change', event => this.fileChosen(event, 'upload'), on);
      this.section.addEventListener('variant:changed', event => {
        this.selectedVariant = event.detail?.variant || null;
        this.updateEligibility();
        this.paint();
      }, on);
      document.addEventListener('kdk:spaces:changed', event => {
        const rows = event.detail?.rows;
        // A local save should be the space shown on the next product page too.
        if (Array.isArray(rows)) rememberSpace(rows[0]?.id || '');
        this.refresh(rows);
      }, on);
      window.addEventListener('storage', event => {
        if ([selectedKey, String(cfg.storageNamespace || 'kdk_spaces_lab_v1') + ':index'].includes(event.key)) this.refresh();
      }, on);
      window.addEventListener('pageshow', event => { if (event.persisted) { this.syncVariant(); this.refresh(); } }, on);
      document.addEventListener('visibilitychange', () => this.scheduleVisibility(), on);
      document.addEventListener('kdk:preview:open', () => this.scheduleVisibility(), on);
      document.addEventListener('kdk:preview:close', () => this.scheduleVisibility(), on);
      document.addEventListener('kdk:preview:analytics-ready', () => {
        this.measureAvailable();
        this.scheduleVisibility();
      }, on);
      document.addEventListener('visitorConsentCollected', () => {
        clearTimeout(this.visibilityTimer);
        this.visibilityTimer = null;
        if (!window.KdkPreviewAnalytics?.debug()?.analyticsAllowed) {
          this.availableSent = false; this.impressionSent = false; this.cacheViews.clear();
        }
        this.measureAvailable();
        this.scheduleVisibility();
      }, on);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.ratio = entries[0]?.intersectionRatio || 0;
          this.scheduleVisibility();
        }, { threshold: [0, .5, 1] });
        this.observer.observe(root);
      }
      if ('ResizeObserver' in window) {
        this.sizeObserver = new ResizeObserver(() => this.resize());
        this.sizeObserver.observe(this.root.querySelector('.kdk-gallery-preview__surface'));
      }
      this.syncVariant();
      if (indexHasSpaces()) this.refresh(); else this.paint();
    }
    text(key) { return this.strings[key] || ''; }
    frameText(key, frame) { return this.text(key).split('__KDK_FRAME__').join(String(frame || '')); }
    shell() { return this.section.querySelector('[data-kdk-preview-shell],.kdk-pdp-preview'); }
    parameters() {
      return { product_id: this.productId, variant_id: validId(this.variant?.id), gallery_state: this.state,
        space_id: this.selected?.id || '', space_count: this.rows.length };
    }
    track(action, extra = {}) { return window.KdkPreviewAnalytics?.gallery(action, { ...this.parameters(), ...extra }) === true; }
    report(code) { window.KdkPreviewAnalytics?.galleryError(code, this.parameters()); }
    syncVariant() {
      const id = this.section.querySelector('.ProductForm [name="id"]')?.value || this.root.dataset.initialVariantId;
      this.selectedVariant = (this.product.variants || []).find(value => String(value.id) === String(id)) || null;
      this.updateEligibility();
    }
    updateEligibility() {
      const bridge = window.KdkGalleryPreviewBridge;
      this.variant = bridge?.resolveVariant(this.product, this.selectedVariant?.id) || null;
      const available = this.selectedVariant && this.selectedVariant.available !== false;
      this.eligible = Boolean(this.variant && validId(this.variant.id) && this.shell());
      const notice = this.root.querySelector('[data-gallery-eligibility]');
      if (notice) { notice.hidden = this.eligible; notice.textContent = !available ? this.text('unavailable') : this.text('unframed'); }
      const label = this.root.querySelector('[data-gallery-variant]');
      if (label) label.textContent = bridge?.frameLabel(this.variant || this.selectedVariant) || '';
      this.syncButtons();
      this.measureAvailable();
      this.scheduleVisibility();
    }
    measureAvailable() {
      if (this.eligible && !this.availableSent) this.availableSent = this.track('gallery_available');
    }
    visible() {
      const owner = this.shell()?.__kdkProductPreviewer;
      return !this.destroyed && this.eligible && !this.painting && !this.busy && this.root.isConnected && this.ratio >= .5 && document.visibilityState === 'visible' &&
        !document.documentElement.hasAttribute('data-kdk-preview-open') && !document.querySelector('dialog[open]') && owner?.modalEl?.hidden !== false;
    }
    scheduleVisibility() {
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = null;
      if (!this.visible()) return;
      const renderKey = this.renderViewKey();
      if (this.impressionSent && (!renderKey || this.cacheViews.has(renderKey))) return;
      this.visibilityTimer = setTimeout(() => {
        this.visibilityTimer = null;
        if (!this.visible()) return;
        if (!this.impressionSent) this.impressionSent = this.track('gallery_impression');
        const key = this.renderViewKey();
        if (key && !this.cacheViews.has(key) && this.track('gallery_cached_view', {
          variant_id: validId(this.savedRenderItem(this.selected)?.variantId)
        })) this.cacheViews.add(key);
      }, 1000);
    }
    async store() {
      if (window.KdkSpacesStore) return window.KdkSpacesStore;
      if (window.KdkSpaces?.ensureStore) return window.KdkSpaces.ensureStore();
      await window.KdkSpaces?.ensure();
      if (!window.KdkSpacesStore) throw new Error('storage');
      return window.KdkSpacesStore;
    }
    async refresh(providedRows) {
      if (this.destroyed) return;
      const seq = ++this.refreshSequence;
      try {
        const store = await this.store();
        const rows = Array.isArray(providedRows) ? providedRows : await store.list();
        if (seq !== this.refreshSequence || this.destroyed) return;
        this.rows = rows.slice(0, 5);
        const preferred = selectedSpace();
        this.selected = this.rows.find(row => row.id === preferred) || this.rows[0] || null;
        if (this.selected && preferred !== this.selected.id) rememberSpace(this.selected.id);
        if (!this.selected) rememberSpace('');
        const live = new Set(this.rows.map(row => row.id + ':' + row.revision));
        for (const [key, url] of this.urls) {
          const parts = key.split(':'); parts.pop();
          if (!live.has(parts.join(':'))) { URL.revokeObjectURL(url); this.urls.delete(key); }
        }
        await this.paint();
      } catch (_) {
        if (seq !== this.refreshSequence || this.destroyed) return;
        this.rows = []; this.selected = null;
        await this.paint();
        this.status(this.text('storage_error'), 'error');
        this.report('storage');
      }
    }
    savedRenderItem(row) {
      return row && Number(row.phase) === 2 && row.items?.length === 1 &&
        String(row.items[0].productId) === this.productId && validId(row.items[0].variantId) ? row.items[0] : null;
    }
    matches(row) {
      const item = this.savedRenderItem(row);
      return Boolean(item && this.variant && String(item.variantId) === String(this.variant.id));
    }
    renderViewKey() {
      const item = this.state === 'render' && this.savedRenderItem(this.selected);
      return item ? this.selected.id + ':' + this.selected.revision + ':' + item.variantId : '';
    }
    imageKey(row, role) { return row.id + ':' + row.revision + ':' + role; }
    async image(row, role) {
      const key = this.imageKey(row, role);
      if (this.urls.has(key)) return this.urls.get(key);
      const blob = await (await this.store()).image(row.id, role);
      if (this.destroyed) throw new Error('cancelled');
      const url = URL.createObjectURL(blob);
      if (this.urls.has(key)) { URL.revokeObjectURL(url); return this.urls.get(key); }
      this.urls.set(key, url);
      return url;
    }
    async paint() {
      const seq = ++this.sequence;
      const chosen = this.selected;
      let state = chosen ? (this.savedRenderItem(chosen) ? 'render' : 'room') : 'empty';
      const role = state === 'render' ? 'render' : 'source';
      let source = chosen ? this.urls.get(this.imageKey(chosen, role)) || '' : '';
      this.painting = Boolean(chosen && !source);
      clearTimeout(this.visibilityTimer);
      this.visibilityTimer = null;
      if (!source) {
        this.root.querySelector('[data-gallery-photo]').hidden = true;
        this.root.querySelector('[data-gallery-image]').removeAttribute('src');
        this.root.querySelector('[data-gallery-saved-title]').hidden = true;
        this.root.querySelector('[data-gallery-render-note]').hidden = true;
        this.root.querySelector('[data-gallery-saved-actions]').hidden = true;
      }
      this.root.querySelector('[data-gallery-intro]').hidden = Boolean(chosen);
      this.root.querySelector('[data-gallery-new-actions]').hidden = Boolean(chosen);
      this.root.dataset.galleryState = this.painting ? 'loading' : state;
      if (this.painting || this.busy) this.root.setAttribute('aria-busy', 'true');
      if (this.painting && !this.busy) this.status(this.text('loading'));
      this.syncButtons();
      if (chosen && !source) {
        try { source = await this.image(chosen, role); }
        catch (_) {
          if (state === 'render') {
            try { source = await this.image(chosen, 'source'); state = 'room'; }
            catch (_) { state = 'empty'; }
          } else state = 'empty';
          this.report('storage');
        }
      }
      if (seq !== this.sequence || this.destroyed) return;
      this.painting = false;
      if (!this.busy) {
        this.root.removeAttribute('aria-busy');
        if (this.root.querySelector('[data-gallery-status]').textContent === this.text('loading')) this.status('');
      }
      this.state = state;
      this.root.dataset.galleryState = state;
      const saved = state !== 'empty';
      this.root.querySelector('[data-gallery-intro]').hidden = saved;
      this.root.querySelector('[data-gallery-photo]').hidden = !saved;
      this.root.querySelector('[data-gallery-new-actions]').hidden = saved;
      this.root.querySelector('[data-gallery-saved-actions]').hidden = !saved;
      const title = this.root.querySelector('[data-gallery-saved-title]');
      title.hidden = !saved;
      const renderedItem = state === 'render' ? this.savedRenderItem(chosen) : null;
      const renderedVariant = renderedItem && (this.product.variants || []).find(variant => String(variant.id) === String(renderedItem.variantId));
      const renderedFrame = window.KdkGalleryPreviewBridge?.frameLabel(renderedVariant) || '';
      const selectedFrame = window.KdkGalleryPreviewBridge?.frameLabel(this.variant) || '';
      const exact = state === 'render' && this.matches(chosen);
      title.textContent = state === 'render' ? (renderedFrame ? this.frameText('render_frame_title', renderedFrame) : this.text('render_title')) : this.text('room_title');
      const renderNote = this.root.querySelector('[data-gallery-render-note]');
      const hasNewOption = state === 'render' && !exact && this.eligible;
      renderNote.textContent = hasNewOption ? (renderedFrame && renderedFrame === selectedFrame ? this.text('new_variant_note') : this.frameText('new_frame_note', selectedFrame)) : '';
      renderNote.hidden = !hasNewOption;
      this.root.querySelector('[data-gallery-variant]').hidden = state === 'render';
      const image = this.root.querySelector('[data-gallery-image]');
      if (source && saved) {
        image.alt = this.text(state === 'render' ? 'render_alt' : 'source_alt');
        image.src = source;
      } else image.removeAttribute('src');
      this.root.querySelector('[data-gallery-badge]').textContent = this.text(state === 'render' ? 'render_badge' : 'room_badge');
      this.root.querySelector('[data-gallery-primary-label]').textContent = this.text(state === 'render' ? (exact ? 'open_render' : 'new_simulation') : 'use_space');
      const note = this.root.querySelector('[data-gallery-note]');
      note.textContent = state === 'render' ? this.text('simulation_note') : '';
      note.hidden = state !== 'render';
      this.syncButtons();
      this.resize();
      this.measureAvailable();
      this.scheduleVisibility();
    }
    syncButtons() {
      this.root.querySelectorAll('[data-gallery-action]').forEach(control => {
        const action = control.dataset.galleryAction;
        control.disabled = this.busy || this.painting || (['camera', 'upload', 'use'].includes(action) && !this.eligible);
      });
    }
    status(message, tone = 'info') {
      const output = this.root.querySelector('[data-gallery-status]');
      output.textContent = message || ''; output.dataset.tone = tone; this.resize();
    }
    resize() {
      requestAnimationFrame(() => {
        if (this.destroyed) return;
        const slideshow = this.section.querySelector('.Product__Slideshow');
        const carousel = slideshow && window.Flickity?.data(slideshow);
        if (carousel?.isActive) {
          // Prestige can skip resize when the window width is unchanged, even
          // after this card or its container changes size.
          carousel.resize();
          carousel.getSize();
          carousel.reposition();
          carousel.setGallerySize();
        }
      });
    }
    navigate(event) {
      const target = event.target.closest?.('[data-kdk-gallery-thumb]');
      if (!target || target.getAttribute('aria-controls') !== this.root.id) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const slideshow = this.section.querySelector('.Product__Slideshow');
      const carousel = slideshow && window.Flickity?.data(slideshow);
      if (carousel?.isActive) {
        // Prestige hides settled, non-selected slides on desktop.
        this.section.querySelectorAll('.Product__SlideItem--hidden').forEach(slide => slide.classList.remove('Product__SlideItem--hidden'));
        carousel.getSize();
        carousel.reposition();
        carousel.select(Number(target.dataset.mediaIndex));
      }
      else {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.root.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
      this.root.focus({ preventScroll: true });
    }
    handleAction(event) {
      const target = event.target.closest?.('[data-gallery-action]');
      if (!target || !this.root.contains(target) || target.disabled || this.busy) return;
      event.preventDefault(); event.stopPropagation();
      const action = target.dataset.galleryAction;
      if (action === 'camera' || action === 'upload') this.pickFile(action);
      else if (action === 'use') this.launchSaved(target);
      else if (action === 'spaces') { this.track('gallery_change_space'); this.openPicker(true, target); }
      else if (action === 'new') this.openPicker(false, target);
    }
    pickFile(source) {
      if (!this.eligible || this.busy) return;
      if (this.rows.length >= 5) { this.status(this.text('capacity')); return; }
      this.track(source === 'camera' ? 'gallery_camera' : 'gallery_upload');
      this.status('');
      this.root.querySelector(source === 'camera' ? '[data-gallery-camera-input]' : '[data-gallery-upload-input]')?.click();
    }
    fileChosen(event, source) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !this.eligible || this.busy) return;
      this.launch({ file, source });
    }
    launchSaved(trigger) {
      if (!this.selected || !this.eligible || this.busy) return;
      this.track('gallery_use_space');
      this.launch({ spaceId: this.selected.id, source: 'saved_space', trigger });
    }
    async launch(options) {
      if (this.busy || !this.eligible) return;
      this.busy = true; this.syncButtons();
      this.root.setAttribute('aria-busy', 'true');
      this.status(this.text('loading'));
      clearTimeout(this.visibilityTimer);
      try {
        const bridge = window.KdkGalleryPreviewBridge;
        if (!bridge) throw new Error('load_failed');
        const result = await bridge.open({ ...options, shell: this.shell(), variantId: String(this.variant.id), selectedVariantId: String(this.selectedVariant.id) });
        if (this.destroyed) return;
        if (result?.status === 'ready' || result?.status === 'cancelled') this.status('');
        else if (result?.status === 'capacity') this.status(this.text('capacity'));
        else if (result?.status === 'busy') this.status(this.text('busy'));
        else {
          const code = result?.code || 'load_failed';
          this.status(this.text(code === 'gallery_variant_unavailable' || code === 'invalid_product' ? 'unavailable' : code === 'photo_decode' ? 'image_error' : code === 'not_found' || code === 'space_load_failed' ? 'storage_error' : 'load_error'), 'error');
          this.report(code === 'photo_decode' ? 'photo_decode' : code === 'not_found' || code === 'space_load_failed' ? 'storage' : 'load_failed');
        }
      } catch (_) {
        if (!this.destroyed) { this.status(this.text('load_error'), 'error'); this.report('load_failed'); }
      } finally {
        if (!this.destroyed) { this.busy = false; this.root.removeAttribute('aria-busy'); this.syncButtons(); this.scheduleVisibility(); }
      }
    }
    closePicker() {
      if (this.dialog?.open) this.dialog.close();
      this.dialog?.remove(); this.dialog = null;
    }
    async openPicker(showSpaces, opener) {
      this.closePicker();
      const atCapacity = this.rows.length >= 5;
      showSpaces = showSpaces || atCapacity;
      const dialog = node('dialog', 'kdk-gallery-picker');
      this.dialog = dialog;
      dialog.setAttribute('data-hj-suppress', '');
      const headingId = this.root.id + '-picker-title';
      dialog.setAttribute('aria-labelledby', headingId);
      const header = node('div', 'kdk-gallery-picker__header');
      const title = node('h2', 'kdk-gallery-picker__title', this.text(showSpaces ? 'choose_space' : 'new_photo_title')); title.id = headingId;
      const close = button('kdk-gallery-picker__close', '×'); close.setAttribute('aria-label', this.text('close'));
      close.addEventListener('click', () => dialog.close());
      header.append(title, close);
      const body = node('div', 'kdk-gallery-picker__body');
      const list = node('div', 'kdk-gallery-picker__list');
      if (atCapacity) body.appendChild(node('p', 'kdk-gallery-preview__notice', this.text('capacity')));
      if (showSpaces) body.appendChild(list);
      const actions = node('div', 'kdk-gallery-preview__actions');
      for (const kind of ['camera', 'upload']) {
        const control = button('kdk-gallery-preview__button' + (kind === 'camera' ? ' kdk-gallery-preview__button--primary' : ''), this.text(kind));
        control.disabled = !this.eligible || atCapacity;
        control.addEventListener('click', () => { dialog.close(); this.pickFile(kind); });
        actions.appendChild(control);
      }
      body.append(actions);
      dialog.append(header, body);
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('close', () => {
        dialog.remove(); if (this.dialog === dialog) this.dialog = null;
        if (opener?.isConnected) opener.focus({ preventScroll: true });
        this.scheduleVisibility();
      }, { once: true });
      document.body.appendChild(dialog);
      dialog.showModal();
      if (!showSpaces) return;
      try {
        const rows = await (await this.store()).list();
        if (!dialog.open || this.destroyed) return;
        this.rows = rows.slice(0, 5);
        if (!this.rows.length) { list.appendChild(node('p', '', this.text('no_spaces'))); return; }
        for (const row of this.rows) {
          const tile = button('kdk-gallery-picker__space');
          const image = node('img'); image.alt = ''; image.width = 256; image.height = 192; image.decoding = 'async';
          tile.append(image, node('span', '', String(row.name || '').slice(0, 40)), node('small', '', this.text('use_space')));
          tile.disabled = !this.eligible;
          if (row.id === this.selected?.id) tile.setAttribute('aria-current', 'true');
          tile.addEventListener('click', () => {
            this.selected = row; rememberSpace(row.id); dialog.close();
            this.state = this.savedRenderItem(row) ? 'render' : 'room';
            this.paint(); this.launchSaved(opener);
          });
          list.appendChild(tile);
          this.image(row, 'thumb').then(url => { if (dialog.open) image.src = url; }).catch(() => {});
        }
      } catch (_) {
        if (dialog.open) list.appendChild(node('p', 'kdk-gallery-preview__notice', this.text('storage_error')));
        this.report('storage');
      }
    }
    destroy() {
      this.destroyed = true; this.sequence++; this.refreshSequence++;
      this.abort.abort(); this.observer?.disconnect(); this.sizeObserver?.disconnect(); clearTimeout(this.visibilityTimer);
      this.closePicker();
      for (const url of this.urls.values()) URL.revokeObjectURL(url);
      this.urls.clear(); instances.delete(this.root);
    }
  }
  function init(scope = document) {
    const roots = [...(scope.querySelectorAll?.('[data-kdk-gallery-preview]') || [])];
    if (scope.matches?.('[data-kdk-gallery-preview]')) roots.unshift(scope);
    for (const root of roots) if (!instances.has(root)) instances.set(root, new GalleryPreview(root));
  }
  window.KdkGalleryPreview = { init, version: 1 };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) if (event.target.contains(root)) instance.destroy();
  });
})();
